import { Link } from 'react-router-dom';
import { ClipboardPen, Plus } from 'lucide-react';
import { tr } from '@/lib/i18n-core';
import { useAuth } from '@/lib/auth';
import { dateTime } from '@/lib/utils';
import { Badge, Button, statusLabel } from '@/components/ui/primitives';
import { ShellEmpty, ShellSectionHeading } from '@/components/clinical-shell/ShellPanel';
import { admissionIsOpen, ipdEmrPath, type Admission } from '../../admission/types';

export function NursingNotesSection({ admission }: { admission: Admission }) {
  const { can } = useAuth();
  const current = admissionIsOpen(admission);
  const addTo = `/ipd/admissions/${admission.id}/notes/new?kind=nursing&returnTo=${encodeURIComponent(ipdEmrPath(admission.id, 'nursing-notes'))}`;

  return (
    <>
      <ShellSectionHeading
        icon={ClipboardPen}
        title={tr('ipd', 'nursing_notes_and_handovers')}
        description={tr('ipd', 'ws_nursing_notes_desc')}
        actions={
          current && can('nursing.note.write') ? (
            <Link to={addTo}>
              <Button size="sm">
                <Plus className="h-4 w-4" aria-hidden /> {tr('ipd', 'add_note')}
              </Button>
            </Link>
          ) : null
        }
      />
      {admission.nursingNotes.length === 0 && <ShellEmpty>{tr('ipd', 'no_nursing_notes_yet')}</ShellEmpty>}
      <div className="space-y-3">
        {admission.nursingNotes.map((note) => (
          <article key={note.id} className="rounded-2xl border bg-card p-4 text-sm shadow-sm">
            <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>{dateTime(note.createdAt)}</span>
              <Badge variant="secondary">{statusLabel(note.kind)}</Badge>
              {note.shift && <Badge variant="muted">{tr('ipd', 'ws_shift_x', { shift: statusLabel(note.shift) })}</Badge>}
            </div>
            {note.situation && (
              <p>
                <strong>{tr('ipd', 'situation')}:</strong> {note.situation}
              </p>
            )}
            {note.recommendation && (
              <p>
                <strong>{tr('ipd', 'recommendation')}:</strong> {note.recommendation}
              </p>
            )}
            {note.body && <p className="whitespace-pre-line">{note.body}</p>}
          </article>
        ))}
      </div>
    </>
  );
}
